import { useState } from 'react';
import { AuthenticatorType, GeneralSignInInfoResult } from '@mews-commander/data-access';
import { externalRedirect } from '@mews-commander/utils';
import { useHasPasskeySupport, useSignInWithPasskey } from './useSignInWithPasskey';
import { useGetSignInInfo } from './useGetSignInInfo';

export type FormStep = 'email' | 'credentials' | '2fa';

interface SignInAuthenticatorData {
    type: AuthenticatorType;
    email: string;
}

export interface SignInEventHandlers {
    onEmailSubmit: ({ signInData, nameOrEmail }: { signInData: GeneralSignInInfoResult; nameOrEmail: string }) => void;
    onPasswordReset: () => void;
    onTwoFactorCheck: (authenticatorData: SignInAuthenticatorData) => void;
}

export function useSignInRouter() {
    const [signInInfo, setSignInInfo] = useState<GeneralSignInInfoResult | undefined>(undefined);
    const [isCheckingPasskey, setIsCheckingPasskey] = useState<boolean>(false);
    const [authenticationData, setAuthenticationDAta] = useState<SignInAuthenticatorData | undefined>(undefined);
    const [step, setStep] = useState<FormStep>('email');
    const [email, setEmail] = useState<string>('');

    const passkeysEnabled = useHasPasskeySupport();
    const getSignInInfo = useGetSignInInfo();
    const trySignInWithPasskey = useSignInWithPasskey({ setIsCheckingPasskey });

    const onEmailSubmit = async ({
        signInData,
        nameOrEmail,
    }: {
        signInData: GeneralSignInInfoResult;
        nameOrEmail: string;
    }) => {
        const { ssoInfo, passkeyInfo } = signInData;
        setSignInInfo(signInData);
        setEmail(nameOrEmail);

        if (ssoInfo?.enforceSingleSignOn && ssoInfo.signInUrl) {
            externalRedirect(ssoInfo.signInUrl);
            return;
        }

        if (passkeysEnabled && passkeyInfo) {
            await trySignInWithPasskey({ passkeyInfo, nameOrEmail });
        }

        setStep('credentials');
    };

    const onPasskeyRetry = async () => {
        const newSignInInfo = await getSignInInfo({ nameOrEmail: email });
        setSignInInfo(newSignInInfo);

        if (newSignInInfo?.passkeyInfo) {
            await trySignInWithPasskey({ passkeyInfo: newSignInInfo.passkeyInfo, nameOrEmail: email });
        }
    };

    const onPasswordReset = () => {
        setSignInInfo(undefined);
        setStep('email');
    };

    const onTwoFactorCheck = (newAuthenticatorData: SignInAuthenticatorData) => {
        setAuthenticationDAta(newAuthenticatorData);
        setStep('2fa');
    };

    return {
        step,
        signInInfo,
        isCheckingPasskey,
        authenticationData,
        onEmailSubmit,
        onPasskeyRetry,
        onPasswordReset,
        onTwoFactorCheck,
    };
}
